import type { ErrorEvent, EventHint } from '@sentry/react';

import { isAxiosError, type InternalAxiosRequestConfig } from 'axios';

const OPERATION_NAME = /^\s*(?:query|mutation|subscription)\s+(\w+)/;

const parseJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

const toPayload = (data: unknown): unknown => {
  if (typeof data === 'string') return parseJson(data);
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    const operations = data.get('operations');
    return typeof operations === 'string' ? parseJson(operations) : undefined;
  }
  return data;
};

const getOperationName = (data: unknown): string | undefined => {
  const payload = toPayload(data);
  if (typeof payload !== 'object' || payload === null || !('query' in payload)) return undefined;
  const { query } = payload;
  return typeof query === 'string' ? OPERATION_NAME.exec(query)?.[1] : undefined;
};

const trimTrailingSlashes = (value: string): string => {
  let end = value.length;
  while (end > 0 && value[end - 1] === '/') end -= 1;
  return value.slice(0, end);
};

const getRequestUrl = ({ baseURL, url = '' }: Pick<InternalAxiosRequestConfig, 'baseURL' | 'url'>): string => {
  const isAbsolute = /^[a-z][a-z\d+.-]*:\/\//i.test(url);
  const full = isAbsolute || !baseURL ? url : `${trimTrailingSlashes(baseURL)}/${url.replace(/^\/+/, '')}`;
  return full.split(/[?#]/)[0];
};

export const addAxiosContext = (event: ErrorEvent, hint: EventHint): ErrorEvent => {
  const error = hint.originalException;
  if (!isAxiosError(error)) return event;

  const { config, response } = error;
  const operation = getOperationName(config?.data);

  return {
    ...event,
    tags: {
      ...event.tags,
      'axios.code': error.code,
      ...(config?.method && { 'http.method': config.method.toUpperCase() }),
      ...(config?.url && { 'http.url': getRequestUrl(config) }),
      ...(operation && { 'graphql.operation': operation }),
      ...(response && { 'http.status_code': response.status }),
      'navigator.online': navigator.onLine
    }
  };
};
